import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const SSOSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);

  const [config, setConfig] = useState({
    sso_enabled: false,
    sso_provider: 'none',
    client_id: '',
    client_secret: '',
    tenant_id: '',
    issuer: '',           // ✅ Added issuer
    redirect_uri: '',
    email_domain: '',
    connection_verified: false,
  });

  // Fetch available providers and current config
  useEffect(() => {
    fetchProviders();
    fetchSSOConfig();
  }, []);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/sso/providers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      if (response.data.success) {
        setAvailableProviders(response.data.providers);
      }
    } catch (err) {
      //console.error('Error fetching providers:', err);
    }
  };

  const fetchSSOConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/sso/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      if (response.data.success) {
        setConfig({
          sso_enabled: response.data.data.sso_enabled || false,
          sso_provider: response.data.data.provider || 'none',
          client_id: response.data.data.client_id || '',
          tenant_id: response.data.data.tenant_id || '',
          issuer: response.data.data.issuer || '',          // ✅ Added issuer
          redirect_uri: response.data.data.redirect_uri || '',
          email_domain: response.data.data.email_domain || '',
          connection_verified: response.data.data.connection_verified || false,
          client_secret: '', // Don't show secret
        });
      }
    } catch (err) {
      //console.error('Error fetching SSO config:', err);
      if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Company not created yet');
      } else {
        setError('Failed to load SSO configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);

      const token = localStorage.getItem('token');
      
      const data = {
        sso_enabled: config.sso_enabled,
        sso_provider: config.sso_provider,
        client_id: config.client_id || undefined,
        tenant_id: config.tenant_id || undefined,
        issuer: config.issuer || undefined,        // ✅ Added issuer
        redirect_uri: config.redirect_uri || undefined,
        email_domain: config.email_domain || undefined,
      };

      if (config.client_secret) {
        data.client_secret = config.client_secret;
      }

      const response = await axios.put(`${API_URL}/api/sso/config`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      if (response.data.success) {
        setSuccess('SSO configuration saved successfully!');
        setConfig(prev => ({ ...prev, client_secret: '' }));
        await fetchSSOConfig();
      }
    } catch (err) {
      //console.error('Error saving SSO config:', err);
      setError(err.response?.data?.message || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setError(null);
      setTestResult(null);

      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/sso/config/test`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });

      if (response.data.success) {
        setTestResult({
          success: true,
          message: response.data.message || 'Connection test successful!',
        });
        await fetchSSOConfig();
      }
    } catch (err) {
      //console.error('Error testing connection:', err);
      setTestResult({
        success: false,
        message: err.response?.data?.message || 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  // Check if the selected provider requires an issuer
  const selectedProvider = availableProviders.find(p => p.id === config.sso_provider);
  const requiresIssuer = selectedProvider?.requiresIssuer || false;

  // Check if configuration is complete enough to test
  const canTestConnection = config.client_id && 
    (config.sso_provider !== 'microsoft' || config.tenant_id) &&
    (!requiresIssuer || config.issuer);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            SSO Configuration
          </Typography>
          {config.connection_verified && (
            <Chip 
              icon={<CheckCircleIcon />} 
              label="Verified" 
              color="success" 
              size="small"
            />
          )}
          {!config.connection_verified && config.sso_enabled && (
            <Chip 
              icon={<ErrorIcon />} 
              label="Not Verified" 
              color="warning" 
              size="small"
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {testResult && (
          <Alert 
            severity={testResult.success ? 'success' : 'error'} 
            sx={{ mb: 3 }}
            onClose={() => setTestResult(null)}
          >
            {testResult.message}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Enable SSO Toggle */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Enable SSO
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow users to sign in with their identity provider
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.sso_enabled}
                        onChange={handleChange('sso_enabled')}
                        color="primary"
                      />
                    }
                    label={config.sso_enabled ? 'Enabled' : 'Disabled'}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* SSO Provider Selection */}
          <Grid item xs={12}>
            <TextField
              select
              label="SSO Provider"
              fullWidth
              value={config.sso_provider}
              onChange={handleChange('sso_provider')}
              disabled={!config.sso_enabled}
              helperText="Select the identity provider for SSO"
            >
              {availableProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                  {provider.requiresIssuer && ' (Requires Issuer URL)'}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Configuration Fields - Only show if SSO is enabled and provider selected */}
          {config.sso_enabled && config.sso_provider !== 'none' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Provider Configuration
                  </Typography>
                </Divider>
              </Grid>

              {/* Client ID */}
              <Grid item xs={12}>
                <TextField
                  label="Client ID"
                  fullWidth
                  value={config.client_id}
                  onChange={handleChange('client_id')}
                  required
                  helperText="The Client ID from your identity provider"
                />
              </Grid>

              {/* Client Secret */}
              <Grid item xs={12}>
                <TextField
                  label="Client Secret"
                  fullWidth
                  type="password"
                  value={config.client_secret}
                  onChange={handleChange('client_secret')}
                  helperText="Enter new client secret to update. Leave blank to keep existing."
                  placeholder="Enter new client secret..."
                />
              </Grid>

              {/* Tenant ID - Only for Microsoft */}
              {config.sso_provider === 'microsoft' && (
                <Grid item xs={12}>
                  <TextField
                    label="Tenant ID"
                    fullWidth
                    value={config.tenant_id}
                    onChange={handleChange('tenant_id')}
                    required
                    helperText="The Tenant ID from your Microsoft Entra Admin Center (or 'common' for multi-tenant)"
                  />
                </Grid>
              )}

              {/* ✅ Issuer URL - For providers that require it */}
              {requiresIssuer && (
                <Grid item xs={12}>
                  <TextField
                    label="Issuer URL"
                    fullWidth
                    value={config.issuer}
                    onChange={handleChange('issuer')}
                    required
                    placeholder={
                      config.sso_provider === 'okta' 
                        ? 'https://your-company.okta.com/oauth2/default'
                        : config.sso_provider === 'auth0'
                        ? 'https://your-domain.auth0.com'
                        : 'https://your-provider.com/.well-known/openid-configuration'
                    }
                    helperText={
                      config.sso_provider === 'okta' 
                        ? 'Okta Issuer URL (e.g., https://your-company.okta.com/oauth2/default)'
                        : config.sso_provider === 'auth0'
                        ? 'Auth0 Domain (e.g., https://your-domain.auth0.com)'
                        : 'The OIDC discovery endpoint URL from your provider'
                    }
                  />
                </Grid>
              )}

              {/* Email Domain */}
              <Grid item xs={12}>
                <TextField
                  label="Email Domain"
                  fullWidth
                  value={config.email_domain}
                  onChange={handleChange('email_domain')}
                  placeholder="e.g., company.com"
                  helperText="Users with this email domain will automatically be redirected to SSO"
                />
              </Grid>

              {/* Redirect URI - Info */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Redirect URI to configure in your provider:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 1, wordBreak: 'break-all' }}>
                    {`${API_URL}/api/auth/sso/callback`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Make sure to add this URI to your identity provider's allowed redirect URIs
                  </Typography>
                </Alert>
              </Grid>

              {/* Test Connection Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<VpnKeyIcon />}
                    onClick={handleTestConnection}
                    disabled={testing || !canTestConnection}
                    sx={{ minWidth: 150 }}
                  >
                    {testing ? <CircularProgress size={24} /> : 'Test Connection'}
                  </Button>
                  {!canTestConnection && (
                    <Typography variant="caption" color="text.secondary">
                      {!config.client_id && 'Enter Client ID'}
                      {config.sso_provider === 'microsoft' && !config.tenant_id && ' and Tenant ID'}
                      {requiresIssuer && !config.issuer && ' and Issuer URL'}
                      {' to test'}
                    </Typography>
                  )}
                  {config.connection_verified && (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="Verified" 
                      color="success" 
                      size="small"
                    />
                  )}
                </Box>
              </Grid>
            </>
          )}

          {/* Save Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ minWidth: 150 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Save Configuration'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SSOSettings;